import { AppstoreAddOutlined, CheckCircleOutlined } from "@ant-design/icons";
import { Button, Popover, Typography } from "antd";
import { useEffect, useMemo, useState } from "react";
import { pickLocalized } from "../i18n/translate.js";

interface BeforeInstallPromptEvent extends Event {
	prompt: () => Promise<void>;
	userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
}

declare global {
	interface Navigator {
		standalone?: boolean;
	}
}

type InstallGuidance = "ios" | "in-app" | "browser";

const tt = (zh: string, en: string) => pickLocalized(zh, en);

function isStandalone() {
	return (
		window.matchMedia?.("(display-mode: standalone)").matches === true ||
		window.navigator.standalone === true
	);
}

function installGuidance(): InstallGuidance {
	const userAgent = window.navigator.userAgent;
	if (
		/MicroMessenger|DingTalk|DingTalkLite|Lark|Feishu|Weibo/i.test(userAgent)
	) {
		return "in-app";
	}
	if (
		/iPhone|iPad|iPod/i.test(userAgent) ||
		(window.navigator.platform === "MacIntel" &&
			window.navigator.maxTouchPoints > 1)
	) {
		return "ios";
	}
	return "browser";
}

/** Portal Shell 统一快捷应用入口：Chromium 调起安装框，其余浏览器提供准确操作指引。 */
export function PwaInstallButton() {
	const [promptEvent, setPromptEvent] =
		useState<BeforeInstallPromptEvent | null>(null);
	const [installed, setInstalled] = useState(() => isStandalone());
	const [guidanceOpen, setGuidanceOpen] = useState(false);
	const guidance = useMemo(() => installGuidance(), []);

	useEffect(() => {
		const onBeforeInstallPrompt = (event: Event) => {
			event.preventDefault();
			setPromptEvent(event as BeforeInstallPromptEvent);
		};
		const onInstalled = () => {
			setPromptEvent(null);
			setInstalled(true);
			setGuidanceOpen(false);
		};
		window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
		window.addEventListener("appinstalled", onInstalled);
		return () => {
			window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
			window.removeEventListener("appinstalled", onInstalled);
		};
	}, []);

	const requestInstall = async () => {
		if (!promptEvent) {
			setGuidanceOpen(true);
			return;
		}
		try {
			await promptEvent.prompt();
			const choice = await promptEvent.userChoice;
			setInstalled(choice.outcome === "accepted");
		} catch {
			setGuidanceOpen(true);
		} finally {
			setPromptEvent(null);
		}
	};

	if (installed) {
		return (
			<span
				className="crest-mobile-appbar-slot"
				aria-label={tt("已添加快捷访问", "App installed")}
			>
				<CheckCircleOutlined
					className="crest-mobile-appbar-installed"
					aria-hidden="true"
				/>
			</span>
		);
	}

	return (
		<Popover
			trigger="click"
			placement="topLeft"
			open={guidanceOpen}
			onOpenChange={(open) => setGuidanceOpen(open && !promptEvent)}
			content={<InstallGuidanceContent guidance={guidance} />}
		>
			<Button
				type="text"
				className="crest-mobile-appbar-action"
				aria-label={tt("添加快捷访问", "Install app")}
				title={tt("添加到手机主屏幕", "Add to Home Screen")}
				icon={<AppstoreAddOutlined />}
				onClick={() => void requestInstall()}
			/>
		</Popover>
	);
}

function InstallGuidanceContent({ guidance }: { guidance: InstallGuidance }) {
	if (guidance === "in-app") {
		return (
			<div className="crest-pwa-guidance">
				<Typography.Text strong>
					{tt("请在系统浏览器中打开", "Open in your browser")}
				</Typography.Text>
				<Typography.Text type="secondary">
					{tt(
						"使用右上角菜单选择“在浏览器中打开”，再添加到主屏幕。",
						"Open this page in Safari or Chrome, then add it to your Home Screen.",
					)}
				</Typography.Text>
			</div>
		);
	}
	if (guidance === "ios") {
		return (
			<div className="crest-pwa-guidance">
				<Typography.Text strong>
					{tt("添加到 iPhone 主屏幕", "Add to iPhone Home Screen")}
				</Typography.Text>
				<Typography.Text type="secondary">
					{tt(
						"点击 Safari 的分享按钮，选择“添加到主屏幕”，并开启“作为 Web App 打开”。",
						"In Safari, tap Share, choose Add to Home Screen, then enable Open as Web App.",
					)}
				</Typography.Text>
			</div>
		);
	}
	return (
		<div className="crest-pwa-guidance">
			<Typography.Text strong>
				{tt("添加快捷访问", "Install CREST")}
			</Typography.Text>
			<Typography.Text type="secondary">
				{tt(
					"打开浏览器菜单，选择“安装应用”或“添加到主屏幕”。",
					"Open the browser menu and choose Install app or Add to Home Screen.",
				)}
			</Typography.Text>
		</div>
	);
}
